<template>
  <teleport to="body">
    <transition-group
      name="toast"
      tag="div"
      class="fixed top-4 right-4 z-50 space-y-2 pointer-events-none"
      style="max-width: 400px;"
    >
      <div
        v-for="toast in toasts"
        :key="toast.id"
        :class="[
          'pointer-events-auto rounded-lg shadow-xl p-4 flex items-start gap-3 min-w-[300px] max-w-full animate-slide-in',
          getToastClass(toast.type)
        ]"
        role="alert"
        :aria-live="toast.type === 'error' ? 'assertive' : 'polite'"
      >
        <div class="flex-shrink-0">
          <icon :name="getIconName(toast.type)" class="w-5 h-5" />
        </div>
        <div class="flex-1 min-w-0">
          <p v-if="toast.title" class="font-semibold text-sm mb-1">{{ toast.title }}</p>
          <p class="text-sm">{{ toast.message }}</p>
        </div>
        <button
          v-if="toast.dismissible !== false"
          @click="removeToast(toast.id)"
          class="flex-shrink-0 text-current opacity-70 hover:opacity-100 transition-opacity"
          aria-label="Dismiss notification"
        >
          <icon name="close" class="w-4 h-4" />
        </button>
      </div>
    </transition-group>
  </teleport>
</template>

<script>
import Icon from './Icon.vue'

export default {
  name: 'Toast',
  components: {
    Icon,
  },
  data() {
    return {
      toasts: [],
      toastId: 0,
    }
  },
  methods: {
    show(message, type = 'info', options = {}) {
      const id = ++this.toastId
      const toast = {
        id,
        message,
        type,
        title: options.title || null,
        duration: options.duration || (type === 'error' ? 6000 : 4000),
        dismissible: options.dismissible !== false,
        onClose: options.onClose || null,
      }

      this.toasts.push(toast)

      // Auto-dismiss
      if (toast.duration > 0) {
        setTimeout(() => {
          this.removeToast(id)
        }, toast.duration)
      }

      return id
    },
    success(message, options = {}) {
      return this.show(message, 'success', options)
    },
    error(message, options = {}) {
      return this.show(message, 'error', options)
    },
    warning(message, options = {}) {
      return this.show(message, 'warning', options)
    },
    info(message, options = {}) {
      return this.show(message, 'info', options)
    },
    removeToast(id) {
      const index = this.toasts.findIndex(t => t.id === id)
      if (index !== -1) {
        const toast = this.toasts[index]
        if (toast.onClose) {
          toast.onClose()
        }
        this.toasts.splice(index, 1)
      }
    },
    getToastClass(type) {
      const classes = {
        success: 'bg-green-500 text-white',
        error: 'bg-red-500 text-white',
        warning: 'bg-yellow-500 text-white',
        info: 'bg-blue-500 text-white',
      }
      return classes[type] || classes.info
    },
    getIconName(type) {
      const icons = {
        success: 'tick',
        error: 'close',
        warning: 'close',
        info: 'file',
      }
      return icons[type] || 'file'
    },
    clear() {
      this.toasts = []
    },
  },
  mounted() {
    // Make toast methods available globally
    if (typeof window !== 'undefined') {
      window.$toast = this
    }
  },
}
</script>

<style scoped>
.toast-enter-active {
  transition: all 0.3s ease-out;
}

.toast-leave-active {
  transition: all 0.3s ease-in;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.toast-move {
  transition: transform 0.3s ease;
}

@keyframes slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in {
  animation: slide-in 0.3s ease-out;
}
</style>

